import cx from "classnames";
import React, { useEffect, useMemo, useRef } from "react";

import HasSeen from "@/mk2/apps/bazaar/components/HasSeen";
import HasSeenAd from "@/mk2/apps/bazaar/components/HasSeenAd";
import { Type } from "@/mk2/apps/bazaar/components/Message";
import { ReviewMessage } from "@/mk2/apps/bazaar/components/ReviewMessage";
import { UserMessage } from "@/mk2/apps/bazaar/components/UserMessage";
import { Viewer } from "@/mk2/apps/bazaar/constants";
import { BazaarMessageEntity, BuyerEntity, ReviewEntity } from "@/mk2/apps/bazaar/schemas";
import { Plan, UserEntity } from "@/mk2/schemas";
import { canManageBazaar, isAuthenticatedAs } from "@/mk/bazaar/common/userUtils";

import styles from "./Messages.mscss";

const READ_TIMEOUT = 3 * 1000; // 3s

interface OwnProps {
    className?: string;
    buyer: BuyerEntity;
    messages: BazaarMessageEntity[];
    reviews: ReviewEntity[];
    requestUser: UserEntity;
    requestUserPermissions: string[];
    showProduct?: boolean;

    onRead(messagesIds: number[]);
}

type Props = OwnProps;

const Messages: React.FunctionComponent<Props> = ({
    className,
    messages,
    reviews,
    buyer,
    requestUser,
    requestUserPermissions,
    showProduct = false,
    onRead,
}) => {
    const containerRef = useRef<HTMLDivElement>();

    const isBuyer = isAuthenticatedAs(requestUser, buyer.user);
    const isSeller = isAuthenticatedAs(requestUser, buyer.product.seller.user);
    const isAdmin = canManageBazaar(requestUserPermissions); // Act as seller

    if (!isBuyer && !isSeller && !isAdmin) {
        throw new Error("Should not happen");
    }

    const messagesWithReviews = useMemo<Array<BazaarMessageEntity | ReviewEntity>>(() => {
        const result = [...messages, ...reviews];
        result.sort(
            (a, b) =>
                Date.parse("sentDate" in a ? a.sentDate : a.date) -
                Date.parse("sentDate" in b ? b.sentDate : b.date),
        );
        return result;
    }, [messages, reviews]);

    useEffect(() => {
        if (!isSeller && !isBuyer) {
            // Admins cannot read messages
            return;
        }

        const toRead = messages.filter((r) => r.isNew && r.isSeller === isBuyer);
        if (!toRead.length) {
            // Nothing to read
            return;
        }

        let timer;
        let observer: IntersectionObserver;

        if (!("IntersectionObserver" in window)) {
            // No support for IntersectionObserver (IE11)
            timer = setTimeout(() => {
                onRead(toRead.map((r) => r.id));
            }, READ_TIMEOUT);
        } else {
            // Mark as read after 5s when first in viewport
            observer = new IntersectionObserver(
                ([entry]: IntersectionObserverEntry[]) => {
                    if (entry.isIntersecting) {
                        timer = setTimeout(() => {
                            onRead(toRead.map((r) => r.id));
                        }, READ_TIMEOUT);
                    }
                },
                {
                    rootMargin: "0px",
                },
            );

            if (containerRef.current) {
                observer.observe(containerRef.current);
            }
        }

        return () => {
            clearTimeout(timer);
            if (observer) {
                observer.unobserve(containerRef.current);
            }
        };
    }, [onRead, messages, reviews, containerRef, isSeller, isBuyer]);

    return (
        <div className={cx(styles.Messages, className)} ref={containerRef}>
            {messagesWithReviews.map((messageOrReview) =>
                "sentDate" in messageOrReview ? (
                    <UserMessage
                        key={messageOrReview.id}
                        message={messageOrReview}
                        type={
                            ((isSeller || (isAdmin && !isBuyer)) && messageOrReview.isSeller) ||
                            (isBuyer && !messageOrReview.isSeller)
                                ? Type.SENT
                                : Type.RECEIVED
                        }
                        showProduct={showProduct}
                    />
                ) : (
                    <ReviewMessage
                        key={messageOrReview.id}
                        review={messageOrReview}
                        type={
                            // Admin acts as seller
                            isAuthenticatedAs(
                                isAdmin && !isBuyer ? buyer.product.seller.user : requestUser,
                                messageOrReview.reviewer,
                            )
                                ? Type.SENT
                                : Type.RECEIVED
                        }
                        showProduct={showProduct}
                    />
                ),
            )}
            {messages.length > 0 &&
                (isAdmin ||
                    requestUser.activePlan === Plan.PRO ||
                    requestUser.activePlan === Plan.PLUS) && (
                    // Admin | Plus | Pro can see when the last message was read
                    <HasSeen
                        className={styles.Messages__hasSeen}
                        message={messages[messages.length - 1]}
                        viewer={isAdmin ? Viewer.ADMIN : isSeller ? Viewer.SELLER : Viewer.BUYER}
                    />
                )}
            {messages.length > 0 &&
                !isAdmin &&
                requestUser.activePlan === Plan.FREE &&
                messages[messages.length - 1].isSeller && (
                    // Free user has ad here
                    <HasSeenAd
                        className={styles.Messages__hasSeen}
                        viewer={isSeller ? Viewer.SELLER : Viewer.BUYER}
                    />
                )}
        </div>
    );
};

Messages.displayName = "Messages";

export default React.memo<Props>(Messages);
